import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/view/components/ui/dialog'
import { Button } from '@/view/components/ui/button'
import { Input } from '@/view/components/ui/input'
import { Textarea } from '@/view/components/ui/textarea'
import { Label } from '@/view/components/ui/label'
import { Switch } from '@/view/components/ui/switch'
import { ColorPalette } from '@/view/components/ColorPalette'
import { TriStateSwitch } from '@/view/components/TriStatesSwitch'
import { useConfigPartner } from './useConfigPartner'
import { ProductImageField } from './ProductImageField'
import { Form, FormMessage } from '@/view/components/ui/form'
import { Link2Icon } from 'lucide-react'
import { AlertDialog } from '@/view/components/ui/alert-dialog'
import { EnableAndDisablePartners } from '../EnableAndDisablePartners'
import { TriStateEnum } from '@/app/entities/TriState'

interface ConfigPartnerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  description?: string
  product: any
}

export const ConfigPartner = ({
  open,
  onOpenChange,
  product,
}: ConfigPartnerProps) => {
  const {
    form,
    handleUpdateProduct,
    isColorModalOpen,
    setIsColorModalOpen,
    isToggleStateModalOpen,
    setIsToggleStateModalOpen,
    partnerColor,
    setPartnerColor,
    isSaving,
    createLink,
    handleRemoveImage,
    productState,
    handleProductStateChange,
    isPatchingState,
    isSuperAdmin,
    setCreateLink,
    handleClose,
    partnerCanEdit,
    handleRemoveImageBlack,
  } = useConfigPartner(product, onOpenChange)

  const { errors } = form.formState

  const externalProduct = product?.externalData

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <Form {...form}>
        <DialogContent
          showCloseButton={false}
          className="max-w-lg"
          variant="center"
        >
          <form
            onSubmit={form.handleSubmit(handleUpdateProduct)}
            className="space-y-4"
          >
            <DialogHeader className="flex flex-row items-center justify-between">
              <DialogTitle>Configurações do produto</DialogTitle>
              <div className="ml-auto">
                {isSuperAdmin ? (
                  <TriStateSwitch
                    value={productState}
                    onChange={handleProductStateChange}
                    disabled={isPatchingState}
                  />
                ) : (
                  <AlertDialog
                    open={isToggleStateModalOpen}
                    onOpenChange={setIsToggleStateModalOpen}
                  >
                    <Switch
                      checked={productState === TriStateEnum.FULL}
                      onCheckedChange={() => setIsToggleStateModalOpen(true)}
                    />

                    <EnableAndDisablePartners
                      isActive={productState === TriStateEnum.FULL}
                      id={product?.name}
                    />
                  </AlertDialog>
                )}
              </div>
            </DialogHeader>

            <div className="space-y-4">
              <div
                className={
                  !partnerCanEdit && !externalProduct?.imageBlack
                    ? 'grid grid-cols-1 gap-2'
                    : 'grid grid-cols-2 gap-2'
                }
              >
                <ProductImageField
                  control={form.control}
                  name="image"
                  placeholderImage={externalProduct?.image}
                  alt="logo do produto"
                  canEdit={partnerCanEdit}
                  className="flex aspect-[3/2] h-full items-center justify-center overflow-hidden p-0"
                  style={{
                    backgroundColor: partnerColor || externalProduct?.color,
                  }}
                  onRemove={handleRemoveImage}
                  overlay={
                    <div
                      onClick={() => setIsColorModalOpen(true)}
                      className="z-99 absolute left-2 top-2 h-6 w-6 cursor-pointer rounded border border-gray-300"
                      style={{
                        backgroundColor: partnerColor || externalProduct?.color,
                      }}
                    />
                  }
                />
                {(partnerCanEdit || externalProduct?.imageBlack) && (
                  <ProductImageField
                    control={form.control}
                    name="imageBlack"
                    placeholderImage={externalProduct?.imageBlack}
                    alt="ícone preto do produto"
                    canEdit={partnerCanEdit}
                    className="flex aspect-[3/2] h-full items-center justify-center overflow-hidden border border-gray-200 bg-white p-0"
                    onRemove={handleRemoveImageBlack}
                  />
                )}
              </div>
              {partnerCanEdit ? (
                <>
                  <div className="space-y-1">
                    <Label htmlFor="config-partner-label">Título</Label>
                    <Input
                      id="config-partner-label"
                      placeholder={externalProduct?.title || 'Título'}
                      {...form.register('label')}
                    />
                    {errors.label?.message != null && (
                      <p className="mt-1 text-xs font-medium text-destructive">
                        {String(errors.label.message)}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="config-partner-subtitle">Subtítulo</Label>
                    <Input
                      id="config-partner-subtitle"
                      placeholder={externalProduct?.subtitle || 'Subtítulo'}
                      {...form.register('subtitle')}
                    />
                    {errors.subtitle && (
                      <FormMessage className="mt-1 text-xs">
                        {errors.subtitle.message}
                      </FormMessage>
                    )}
                  </div>
                </>
              ) : (
                <div className="space-y-1">
                  <p className="text-base font-semibold text-foreground">
                    {externalProduct?.title || '-'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {externalProduct?.subtitle || ''}
                  </p>
                </div>
              )}
              {partnerCanEdit ? (
                <div className="space-y-1">
                  <Label htmlFor="config-partner-description">Descrição</Label>
                  <Textarea
                    id="config-partner-description"
                    placeholder={
                      externalProduct?.description || 'Descrição do produto'
                    }
                    rows={4}
                    {...form.register('description')}
                  />
                  {errors.description && (
                    <FormMessage className="mt-1 text-xs">
                      {errors.description.message}
                    </FormMessage>
                  )}
                </div>
              ) : (
                <div className="space-y-1">
                  <Label>Descrição</Label>
                  <p className="whitespace-pre-line text-sm text-muted-foreground">
                    {externalProduct?.description || '-'}
                  </p>
                </div>
              )}
            </div>
            <ColorPalette
              open={isColorModalOpen}
              onOpenChange={setIsColorModalOpen}
              value={partnerColor || ''}
              onChange={setPartnerColor}
            />

            <div className="border-green01 bg-green02 flex flex-col rounded-xl border-2 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Link2Icon className="h-5 w-5 -rotate-45" />
                  <p className="text-sm font-semibold">Auto Contratação</p>
                </div>

                <Switch
                  checked={createLink}
                  onCheckedChange={setCreateLink}
                  disabled={isSaving}
                />
              </div>

              <hr className="border-green01 my-2 border-t" />

              <p className="text-xs font-medium text-gray-500">
                Permitir que os vendedores criem links para que clientes possam
                acessar e contratar de forma simples e segura a qualquer
                momento, sem precisar da interação do vendedor.
              </p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Fechar
              </Button>
              <Button
                type="submit"
                className="text-white01"
                isLoading={isSaving}
              >
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Form>
    </Dialog>
  )
}
